import Screen from '../Screen';
import React, { FC, useState, useEffect } from 'react';
import styled from 'styled-components/native';
import Fonts from 'theme/fonts';
import Colors from 'theme/colors';
import { useHistory, useParams } from 'react-router-dom';
import SubtleAd from 'components/SubtleAd';
import HowToPractice from 'components/HowToPractice';
import SelectCard from 'components/SelectCard';
import manifestAPI from 'api/manifestAPI';
import { DEFAULT_TOEIC_YEAR } from 'config/defaultYear';

const Hero = styled.View`
  background-color: ${Colors.WHITE};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${Colors.CARD_BORDER};
  padding: 18px 16px;
  margin: 4px 8px 12px;
`;

const HeroTitle = styled(Fonts.Bold)`
  font-size: 22px;
  color: ${Colors.MAIN_COLOR};
  margin-bottom: 6px;
`;

const HeroText = styled(Fonts.Normal)`
  font-size: 15px;
  color: ${Colors.MUTED_TEXT};
  line-height: 22px;
`;

const SectionTitle = styled(Fonts.Bold)`
  font-size: 15px;
  color: ${Colors.TEXT};
  margin: 8px 12px 4px;
`;

const YearRow = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  margin: 8px 4px 4px;
`;

const YearChip = styled.TouchableOpacity`
  background-color: ${Colors.WHITE};
  border-width: 1px;
  border-color: ${Colors.CARD_BORDER};
  border-radius: 20px;
  padding: 8px 14px;
  margin: 4px;
`;

const YearChipText = styled(Fonts.Bold)`
  font-size: 14px;
  color: ${Colors.MAIN_COLOR};
`;

const EmptyText = styled(Fonts.Italic)`
  font-size: 15px;
  margin: 12px;
  color: ${Colors.MUTED_TEXT};
  text-align: center;
`;

const LoadingText = styled(Fonts.Normal)`
  font-size: 14px;
  margin: 12px;
  color: ${Colors.MUTED_TEXT};
  text-align: center;
`;

const DEFAULT_TESTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const TestList: FC = () => {
  const history = useHistory();
  const { year } = useParams();
  const currentYear = year || DEFAULT_TOEIC_YEAR;

  const [testNumbers, setTestNumbers] = useState<number[]>(DEFAULT_TESTS);
  const [otherYears, setOtherYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);

    manifestAPI.getRoot()
      .then((root: any) => {
        if (cancelled) return;
        const fromManifest = ((root && root.years) || []).map((y: any) => `${y}`);
        const uniq = Array.from(new Set(['2020', '2021'].concat(fromManifest)))
          .filter((y: string) => y !== currentYear)
          .sort();
        setOtherYears(uniq);
      })
      .catch(() => {
        if (cancelled) return;
        setOtherYears(['2020', '2021'].filter((y) => y !== currentYear));
      });

    manifestAPI.getYear(currentYear)
      .then((manifest: any) => {
        if (cancelled) return;
        const tests = ((manifest && manifest.tests) || [])
          .map((t: any) => Number(t.test))
          .filter((n: number) => n > 0);
        setTestNumbers(tests.length ? tests : DEFAULT_TESTS);
        setLoadFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setTestNumbers(DEFAULT_TESTS);
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentYear]);

  const openPartListScreen = (testNumber: number) => {
    history.push(`/toeic${currentYear}/test${testNumber}`);
  };

  const openDictation = () => {
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    if (win.location && win.location.assign) win.location.assign('/tools/dictation');
  };

  return (
    <Screen title={`TOEIC ${currentYear}`}>
      <Hero>
        <HeroTitle>Ôn luyện TOEIC Online</HeroTitle>
        <HeroText>
          Luyện Listening & Reading theo từng Part, chấm điểm ngay sau khi nộp bài.
          Phù hợp người mới bắt đầu và ôn trước kỳ thi.
        </HeroText>
      </Hero>

      <HowToPractice />

      <SectionTitle>Luyện thêm</SectionTitle>
      <SelectCard
        title="Luyện nghe chép chính tả — Dictation"
        subtitle="Upload audio, tạo bài bằng TTS hoặc luyện trực tiếp với TOEIC Part 1–4"
        meta="Lặp A–B · chấm từng từ · xử lý trên máy bạn"
        badge="Mới"
        onPress={openDictation}
      />
      <SelectCard
        title="Kiểm tra phát âm"
        subtitle="Đọc theo câu mẫu, AI tô chỗ sai — luyện trên điện thoại cũng được"
        meta="Luyện nói · không phải đề thi"
        badge="Mới"
        onPress={() => history.push('/tools/pronunciation')}
      />

      <SectionTitle>{`Chọn bài Test · TOEIC ${currentYear}`}</SectionTitle>
      {loading && <LoadingText>Đang tải danh sách bài…</LoadingText>}
      {!loading && loadFailed && testNumbers.length === 0 && (
        <EmptyText>{`Chưa có dữ liệu cho TOEIC ${currentYear}.`}</EmptyText>
      )}
      {!loading && testNumbers.map((testNumber) => (
        <SelectCard
          key={`test_${testNumber}`}
          title={`Test ${testNumber}`}
          subtitle="7 phần · Listening + Reading"
          meta="Bấm để chọn Part muốn ôn"
          onPress={() => openPartListScreen(testNumber)}
        />
      ))}

      {otherYears.length > 0 && (
        <>
          <SectionTitle>Đề các năm khác</SectionTitle>
          <YearRow>
            {otherYears.map((otherYear: string) => (
              <YearChip
                key={otherYear}
                onPress={() => history.push(`/toeic${otherYear}`)}
              >
                <YearChipText>{`TOEIC ${otherYear}`}</YearChipText>
              </YearChip>
            ))}
          </YearRow>
        </>
      )}

      <SubtleAd />
    </Screen>
  );
};

export default TestList;
